import { useState, type CSSProperties } from "react";
import type { Integration } from "../integrationTypes";
import { REGION_LABELS } from "../integrationMockData";
import { formatLastSyncedRelative } from "../integrationUtils";
import { IntegrationLogo } from "../../../../components/IntegrationLogo";

/**
 * Single integration card for the Integration transactions catalog.
 *
 * Vertical layout: centered logo + name + region + description +
 * (Learn more link OR last-synced timestamp) + primary action button.
 *
 * Button is a rounded rectangle (10px radius, NOT pill), auto-width
 * (hugs its text), centered at the bottom of the card. "Connect
 * integration" on available cards, "Manage" on connected cards,
 * "Coming soon" (disabled) on coming-soon cards.
 *
 * Connected cards get a subtle green tint + "CONNECTED" chip badge +
 * last-synced relative timestamp.
 */

interface IntegrationCardProps {
  integration: Integration;
  onPrimaryClick: (integration: Integration) => void;
  onLearnMore?: (integration: Integration) => void;
}

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  tealDark: "#0F6E56",
  tealLine: "#BBF7D0",
  tealWash: "#F0FDF4",
};

export function IntegrationCard({
  integration,
  onPrimaryClick,
  onLearnMore,
}: IntegrationCardProps) {
  const [hover, setHover] = useState(false);
  const isConnected = integration.status === "connected";
  const isComingSoon = integration.status === "coming_soon";

  const cardStyle: CSSProperties = {
    background: isConnected
      ? `linear-gradient(180deg, ${PALETTE.tealWash} 0%, #ffffff 60%)`
      : "#ffffff",
    border: `1px solid ${isConnected ? PALETTE.tealLine : PALETTE.line}`,
    borderRadius: 12,
    padding: "28px 20px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 10,
    transition: "box-shadow 160ms, border-color 160ms",
    position: "relative",
    boxShadow: hover ? "0 6px 18px rgba(14, 26, 26, 0.08)" : "none",
  };

  return (
    <div
      style={cardStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {isConnected && <span style={connectedBadgeStyle}>CONNECTED</span>}
      {isComingSoon && <span style={comingSoonBadgeStyle}>COMING SOON</span>}

      <div style={logoWrapStyle}>
        <IntegrationLogo
          brandId={integration.brandId}
          name={integration.name}
        />
      </div>

      <div style={nameStyle}>{integration.name}</div>
      <div style={regionStyle}>{REGION_LABELS[integration.region]}</div>
      <div style={descStyle}>{integration.description}</div>

      {isConnected && integration.lastSyncedAt ? (
        <div style={lastSyncStyle}>
          Last synced {formatLastSyncedRelative(integration.lastSyncedAt)}
        </div>
      ) : (
        onLearnMore && (
          <button
            type="button"
            style={learnMoreStyle}
            onClick={() => onLearnMore(integration)}
          >
            Learn more
          </button>
        )
      )}

      <button
        type="button"
        style={
          isConnected
            ? manageBtnStyle
            : isComingSoon
              ? disabledBtnStyle
              : primaryBtnStyle
        }
        disabled={isComingSoon}
        onClick={() => onPrimaryClick(integration)}
      >
        {isConnected
          ? "Manage"
          : isComingSoon
            ? "Coming soon"
            : "Connect integration"}
      </button>
    </div>
  );
}

const connectedBadgeStyle: CSSProperties = {
  position: "absolute",
  top: 10,
  right: 10,
  background: PALETTE.tealDark,
  color: "#ffffff",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.04em",
  padding: "3px 7px",
  borderRadius: 10,
};

const comingSoonBadgeStyle: CSSProperties = {
  position: "absolute",
  top: 10,
  right: 10,
  background: PALETTE.ink4,
  color: "#ffffff",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.04em",
  padding: "3px 7px",
  borderRadius: 10,
};

const logoWrapStyle: CSSProperties = {
  width: 64,
  height: 64,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 6,
};

const nameStyle: CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: PALETTE.ink,
};

const regionStyle: CSSProperties = {
  fontSize: 11,
  color: PALETTE.ink4,
  fontWeight: 500,
  marginTop: -4,
  letterSpacing: "0.02em",
};

const descStyle: CSSProperties = {
  fontSize: 12.5,
  color: PALETTE.ink3,
  lineHeight: 1.55,
  minHeight: 54,
  margin: "2px 4px 10px",
};

const learnMoreStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.tealDark,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  padding: "0 0 10px",
  fontFamily: "inherit",
};

const lastSyncStyle: CSSProperties = {
  fontSize: 10.5,
  color: PALETTE.ink4,
  fontWeight: 500,
  marginBottom: 10,
};

const primaryBtnStyle: CSSProperties = {
  background: PALETTE.ink,
  color: "#ffffff",
  fontSize: 12.5,
  fontWeight: 600,
  padding: "9px 20px",
  borderRadius: 10,
  border: 0,
  cursor: "pointer",
  boxShadow: "0 1px 2px rgba(14, 26, 26, 0.08)",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const manageBtnStyle: CSSProperties = {
  background: "#ffffff",
  color: PALETTE.ink,
  fontSize: 12.5,
  fontWeight: 600,
  padding: "9px 20px",
  borderRadius: 10,
  border: `1px solid ${PALETTE.line}`,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const disabledBtnStyle: CSSProperties = {
  background: PALETTE.line,
  color: PALETTE.ink4,
  fontSize: 12.5,
  fontWeight: 600,
  padding: "9px 20px",
  borderRadius: 10,
  border: 0,
  cursor: "not-allowed",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};